// Shared per-major breakdown card.
//
// Previously lived inside profile-preview-card.tsx as a private memo'd
// component. Extracted so the post-publish inspection page at
// /releases/profiles/:id can render the same surface and we don't keep two
// drifting copies of the OS-pie + top-GPUs + top-models layout.

import { memo, useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

export type Os = "win" | "mac" | "linux" | "android";

export interface DetectedMajor {
	major: number;
	fileCount: number;
	oses: Record<Os, number>;
	topGpus: Array<{ name: string; count: number }>;
	topModels: Array<{ name: string; count: number }>;
}

const OS_COLORS: Record<string, string> = {
	win: "var(--chart-1)",
	mac: "var(--chart-2)",
	linux: "var(--chart-3)",
	android: "var(--chart-4)",
};

const OS_LABELS: Record<string, string> = {
	win: "Windows",
	mac: "macOS",
	linux: "Linux",
	android: "Android",
};

export const MajorBreakdown = memo(function MajorBreakdown({
	major,
}: {
	major: DetectedMajor;
}) {
	const osData = useMemo(
		() =>
			Object.entries(major.oses)
				.filter(([, v]) => v > 0)
				.map(([k, v]) => ({ name: OS_LABELS[k] ?? k, key: k, value: v })),
		[major.oses],
	);

	return (
		<div className="rounded-lg border bg-muted/30 p-4">
			<div className="mb-3 flex flex-wrap items-baseline gap-2">
				<span className="font-mono text-sm font-semibold">
					Chromium {major.major}
				</span>
				<span className="text-xs text-muted-foreground tabular-nums">
					{major.fileCount.toLocaleString()} files
				</span>
			</div>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<div>
					<div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
						OS distribution
					</div>
					{osData.length === 0 ? (
						<p className="text-xs text-muted-foreground">No OS detected</p>
					) : (
						<div className="flex items-center gap-3">
							<div className="h-24 w-24 shrink-0">
								<ResponsiveContainer width="100%" height="100%">
									<PieChart>
										<Pie
											data={osData}
											dataKey="value"
											innerRadius={24}
											outerRadius={42}
											stroke="none"
										>
											{osData.map((entry) => (
												<Cell
													key={entry.key}
													fill={OS_COLORS[entry.key] ?? "var(--chart-5)"}
												/>
											))}
										</Pie>
										<Tooltip
											contentStyle={{
												background: "var(--popover)",
												border: "1px solid var(--border)",
												borderRadius: 6,
												fontSize: 12,
											}}
										/>
									</PieChart>
								</ResponsiveContainer>
							</div>
							<ul className="space-y-1 text-xs">
								{osData.map((d) => (
									<li key={d.key} className="flex items-center gap-2">
										<span
											className="size-2 rounded-sm"
											style={{
												background: OS_COLORS[d.key] ?? "var(--chart-5)",
											}}
										/>
										<span className="text-muted-foreground">{d.name}</span>
										<span className="ml-auto tabular-nums">
											{d.value.toLocaleString()}
										</span>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
				<div className="grid grid-cols-1 gap-3">
					<TopList title="Top GPUs" items={major.topGpus} />
					<TopList title="Top device models" items={major.topModels} />
				</div>
			</div>
		</div>
	);
});

function TopList({
	title,
	items,
}: {
	title: string;
	items: Array<{ name: string; count: number }>;
}) {
	return (
		<div>
			<div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
				{title}
			</div>
			{items.length === 0 ? (
				<p className="text-xs text-muted-foreground">—</p>
			) : (
				<ul className="space-y-0.5 text-xs">
					{items.map((it) => (
						<li
							key={it.name}
							className="flex items-center justify-between gap-2"
						>
							<span className="truncate font-mono">{it.name}</span>
							<span className="tabular-nums text-muted-foreground">
								{it.count.toLocaleString()}
							</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
